// src/services/guestLimitService.js - Track AI Finder usage for guest users

class GuestLimitService {
  constructor() {
    this.STORAGE_KEY = 'giveaura_guest_ai_usage';
    this.MAX_FREE_TRIES = 4;
    this.FINGERPRINT_KEY = 'giveaura_guest_fp';
  }

  // Generate a simple browser fingerprint
  generateFingerprint() {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('GiveAura', 2, 2);
    
    const canvasData = canvas.toDataURL();
    
    const fingerprint = {
      canvas: this.hashCode(canvasData),
    screen: window.screen
    ? `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`
    : 'unknown',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      platform: navigator.platform,
      userAgent: navigator.userAgent.substring(0, 50), // Truncate for storage
      cores: navigator.hardwareConcurrency || 0,
      memory: navigator.deviceMemory || 0
    };
    
    return this.hashCode(JSON.stringify(fingerprint));
  }

  // Simple hash function
  hashCode(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
  }

  // Get or create browser fingerprint
  getFingerprint() {
    try {
      let fingerprint = localStorage.getItem(this.FINGERPRINT_KEY);
      
      if (!fingerprint) {
        fingerprint = this.generateFingerprint();
        localStorage.setItem(this.FINGERPRINT_KEY, fingerprint);
      }
      
      return fingerprint;
    } catch (error) {
      console.error('Error getting fingerprint:', error);
      // Fallback to session-based tracking
      return this.generateFingerprint();
    }
  }

  // Get usage data for current browser/device
  getUsageData() {
    try {
      const fingerprint = this.getFingerprint();
      const stored = localStorage.getItem(this.STORAGE_KEY);
      
      if (!stored) {
        return { fingerprint, count: 0, firstUsed: Date.now(), lastUsed: Date.now() };
      }
      
      const data = JSON.parse(stored);
      
      // Verify fingerprint matches
      if (data.fingerprint !== fingerprint) {
        // Different device/browser - reset
        return { fingerprint, count: 0, firstUsed: Date.now(), lastUsed: Date.now() };
      }
      
      return data;
    } catch (error) {
      console.error('Error getting usage data:', error);
      return { fingerprint: this.getFingerprint(), count: 0, firstUsed: Date.now(), lastUsed: Date.now() };
    }
  }

  // Save usage data
  saveUsageData(data) {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving usage data:', error);
    }
  }

  // Check if guest can use AI Finder
  canUseAIFinder() {
    const data = this.getUsageData();
    return data.count < this.MAX_FREE_TRIES;
  }

  // Get remaining tries
  getRemainingTries() {
    const data = this.getUsageData();
    return Math.max(0, this.MAX_FREE_TRIES - data.count);
  }

  // Increment usage count
  incrementUsage() {
    const data = this.getUsageData();
    data.count += 1;
    data.lastUsed = Date.now();
    this.saveUsageData(data);
    return data.count;
  }

  // Reset usage (for testing or admin purposes)
  resetUsage() {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(this.FINGERPRINT_KEY);
    } catch (error) {
      console.error('Error resetting usage:', error);
    }
  }

  // Get usage stats
  getUsageStats() {
    const data = this.getUsageData();
    return {
      used: data.count,
      remaining: this.getRemainingTries(),
      total: this.MAX_FREE_TRIES,
      firstUsed: data.firstUsed ? new Date(data.firstUsed) : null,
      lastUsed: data.lastUsed ? new Date(data.lastUsed) : null,
      canUse: this.canUseAIFinder()
    };
  }
}

// Export singleton instance
export const guestLimitService = new GuestLimitService();
export default guestLimitService;